// src/client/components/ToastProvider.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import styles from './Toast.module.css';

type ToastTone = 'info' | 'success' | 'error';
interface Toast { id: number; message: string; tone: ToastTone; }
interface ToastApi { show: (message: string, tone?: ToastTone) => void; }

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Monotonic id counter so two show() calls in the same React batch get
  // distinct ids (Date.now() collides at sub-millisecond resolution and
  // produced duplicate React keys + a cleanup filter that removed both).
  const idRef = useRef(0);
  // Track outstanding dismiss timers so we can clear them on unmount and
  // avoid setState-after-unmount warnings + leaks.
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const show = useCallback((message: string, tone: ToastTone = 'info') => {
    const id = ++idRef.current;
    setToasts((t) => [...t, { id, message, tone }]);
    const timer = setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
      timersRef.current.delete(timer);
    }, 3500);
    timersRef.current.add(timer);
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {/* role="status" on each toast already implies aria-live="polite", so
          we omit aria-live on the container to avoid double announcements
          in NVDA / VoiceOver. */}
      <div className={styles.stack}>
        {toasts.map((t) => (
          <div key={t.id} className={[styles.toast, styles[t.tone]].filter(Boolean).join(' ')} role="status">
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
